import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  MenuItem,
  CircularProgress,
  Alert,
  Divider,
  FormHelperText,
  FormControl,
  InputLabel,
  Select,
  Autocomplete,
  InputAdornment,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { 
  ArrowBack as ArrowBackIcon, 
  Save as SaveIcon, 
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import apiService from '../../services/apiService';

// Esquema de validación
const TransferSchema = Yup.object().shape({
  branch_origin: Yup.number().required('La sucursal de origen es requerida'),
  branch_destiny: Yup.number().required('La sucursal de destino es requerida'),
  client_origin: Yup.number().required('El cliente de origen es requerido'),
  client_destiny: Yup.number().required('El cliente de destino es requerido'),
  currency_type: Yup.string().required('El tipo de moneda es requerido'),
  amount: Yup.number()
    .required('El monto es requerido')
    .positive('El monto debe ser positivo')
    .min(10, 'El monto mínimo es 10'),
  comments: Yup.string()
});

const TransferForm = () => {
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [branches, setBranches] = useState([]);
  const [clients, setClients] = useState([]);
  const [commission, setCommission] = useState(null);
  const [calculatingCommission, setCalculatingCommission] = useState(false);
  
  // Estado para comisiones personalizadas
  const [customCommissions, setCustomCommissions] = useState([]);
  const [openCommissionDialog, setOpenCommissionDialog] = useState(false);
  const [customCommissionValue, setCustomCommissionValue] = useState('');
  const [currentCurrencyType, setCurrentCurrencyType] = useState('PEN');

  // Valores iniciales para el formulario
  const initialValues = {
    branch_origin: '',
    branch_destiny: '',
    client_origin: '',
    client_destiny: '',
    currency_type: 'PEN',
    amount: '',
    comments: ''
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Obtener sucursales
        const branchesData = await apiService.getBranches();
        setBranches(branchesData);
        
        // Obtener clientes
        const clientsData = await apiService.getClients();
        setClients(clientsData);
        
      } catch (err) {
        console.error('Error al cargar datos:', err);
        setError('Error al cargar los datos necesarios. Por favor, intente nuevamente.');
      }
    };
    
    fetchData();
  }, []);

  const handleCancel = () => {
    navigate('/transfers');
  };

  const calculateCommission = async (amount, currencyType) => {
    if (!amount || amount <= 0) {
      setCommission(null);
      return;
    }
    
    try {
      setCalculatingCommission(true);
      const commissionData = await apiService.calculateCommission(amount, currencyType);
      setCommission(commissionData);
      // Actualizar la moneda actual para las comisiones personalizadas
      setCurrentCurrencyType(currencyType);
    } catch (err) {
      console.error('Error al calcular comisión:', err);
      setCommission(null);
    } finally {
      setCalculatingCommission(false);
    }
  };
  
  const handleAddCustomCommission = () => {
    if (!customCommissionValue || isNaN(parseFloat(customCommissionValue)) || parseFloat(customCommissionValue) <= 0) {
      return;
    }
    
    const newCommission = {
      id: Date.now(), // ID temporal para identificar en la lista
      value: parseFloat(customCommissionValue),
      currency_type: currentCurrencyType
    };
    
    setCustomCommissions([...customCommissions, newCommission]);
    handleCloseCommissionDialog();
  };
  
  const handleDeleteCustomCommission = (commissionId) => {
    setCustomCommissions(customCommissions.filter(comm => comm.id !== commissionId));
  };
  
  const handleOpenCommissionDialog = () => {
    setOpenCommissionDialog(true);
    setCustomCommissionValue('');
  };
  
  const handleCloseCommissionDialog = () => {
    setOpenCommissionDialog(false);
    setCustomCommissionValue('');
  };
  
  // Calcular el total con todas las comisiones personalizadas
  const calculateTotalWithCommissions = (amount) => {
    if (!amount || isNaN(parseFloat(amount))) return 0;
    
    const baseAmount = parseFloat(amount);
    const customCommissionsTotal = customCommissions.reduce(
      (total, comm) => total + comm.value, 
      0
    );
    
    return baseAmount + customCommissionsTotal + (commission ? parseFloat(commission.applied_commission) : 0);
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setError(null);
      
      // Preparar fecha de registro
      const registerDate = new Date().toISOString();
      
      // Enviar solicitud de creación de transferencia
      const transferData = {
        ...values,
        register_date: registerDate
      };
      
      const createdTransfer = await apiService.createTransfer(transferData);
      
      // Si hay comisiones personalizadas, crearlas
      if (customCommissions.length > 0) {
        for (const customComm of customCommissions) {
          await apiService.createCustomCommission(createdTransfer.id, customComm.value);
        }
      }
      
      // Redirigir a la vista de detalles de la transferencia
      navigate(`/transfers/view/${createdTransfer.id}`);
      
    } catch (err) {
      console.error('Error al crear transferencia:', err);
      setError('Error al crear la transferencia. Por favor, intente nuevamente.');
      setSubmitting(false);
    }
  };
  
  const formatCurrency = (amount, currency) => {
    if (!amount) return '';
    
    const formatter = new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: currency === 'PEN' ? 'PEN' : 'USD',
      minimumFractionDigits: 2
    });
    
    return formatter.format(amount);
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={3}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleCancel}
          sx={{ mr: 2 }}
        >
          Volver
        </Button>
        <Typography variant="h4">
          Nueva Transferencia
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <Formik
          initialValues={initialValues}
          validationSchema={TransferSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, isSubmitting, values, setFieldValue }) => (
            <Form>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Información de Sucursales
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth error={touched.branch_origin && Boolean(errors.branch_origin)}>
                    <InputLabel id="branch-origin-label">Sucursal de Origen</InputLabel>
                    <Field
                      as={Select}
                      name="branch_origin"
                      labelId="branch-origin-label"
                      label="Sucursal de Origen"
                    >
                      {branches.map((branch) => (
                        <MenuItem key={branch.id} value={branch.id}>
                          {branch.branch_name}
                        </MenuItem>
                      ))}
                    </Field>
                    {touched.branch_origin && errors.branch_origin && (
                      <FormHelperText>{errors.branch_origin}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth error={touched.branch_destiny && Boolean(errors.branch_destiny)}>
                    <InputLabel id="branch-destiny-label">Sucursal de Destino</InputLabel>
                    <Field
                      as={Select}
                      name="branch_destiny"
                      labelId="branch-destiny-label"
                      label="Sucursal de Destino"
                    >
                      {branches.map((branch) => (
                        <MenuItem key={branch.id} value={branch.id}>
                          {branch.branch_name}
                        </MenuItem>
                      ))}
                    </Field>
                    {touched.branch_destiny && errors.branch_destiny && (
                      <FormHelperText>{errors.branch_destiny}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Información de Clientes
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Autocomplete
                    options={clients}
                    getOptionLabel={(option) => 
                      typeof option === 'object' 
                        ? `${option.first_name} ${option.father_last_name} ${option.mother_last_name} - ${option.document_type}: ${option.document_number}`
                        : ''
                    }
                    onChange={(_, newValue) => {
                      setFieldValue('client_origin', newValue ? newValue.id : '');
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Cliente Origen"
                        error={touched.client_origin && Boolean(errors.client_origin)}
                        helperText={touched.client_origin && errors.client_origin}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Autocomplete
                    options={clients}
                    getOptionLabel={(option) => 
                      typeof option === 'object' 
                        ? `${option.first_name} ${option.father_last_name} ${option.mother_last_name} - ${option.document_type}: ${option.document_number}`
                        : ''
                    }
                    onChange={(_, newValue) => {
                      setFieldValue('client_destiny', newValue ? newValue.id : '');
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Cliente Destino"
                        error={touched.client_destiny && Boolean(errors.client_destiny)}
                        helperText={touched.client_destiny && errors.client_destiny}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Información de la Transferencia
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth error={touched.currency_type && Boolean(errors.currency_type)}>
                    <InputLabel id="currency-type-label">Moneda</InputLabel>
                    <Field
                      as={Select}
                      name="currency_type"
                      labelId="currency-type-label"
                      label="Moneda"
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setFieldValue('currency_type', newValue);
                        if (values.amount) {
                          calculateCommission(values.amount, newValue);
                        }
                        setCurrentCurrencyType(newValue);
                      }}
                    >
                      <MenuItem value="PEN">Soles (PEN)</MenuItem>
                      <MenuItem value="USD">Dólares (USD)</MenuItem>
                    </Field>
                    {touched.currency_type && errors.currency_type && (
                      <FormHelperText>{errors.currency_type}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Field
                    as={TextField}
                    name="amount"
                    label="Monto"
                    fullWidth
                    variant="outlined"
                    type="number"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          {values.currency_type === 'PEN' ? 'S/.' : '$'}
                        </InputAdornment>
                      ),
                    }}
                    error={touched.amount && Boolean(errors.amount)}
                    helperText={touched.amount && errors.amount}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      setFieldValue('amount', newValue);
                      calculateCommission(newValue, values.currency_type);
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle1" gutterBottom>
                      Comisiones
                    </Typography>
                    <Button
                      startIcon={<AddIcon />}
                      color="primary"
                      onClick={handleOpenCommissionDialog}
                    >
                      Añadir Comisión Personalizada
                    </Button>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  
                  {/* Comisión calculada automáticamente */}
                  {commission && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        Comisión automática: {calculatingCommission ? 'Calculando...' : formatCurrency(commission.applied_commission, values.currency_type)}
                      </Typography>
                    </Alert>
                  )}
                  
                  {/* Lista de comisiones personalizadas */}
                  {customCommissions.length > 0 && (
                    <Box mb={2}>
                      <List>
                        {customCommissions.map((comm) => (
                          <ListItem key={comm.id} divider>
                            <ListItemText
                              primary={`Comisión personalizada: ${formatCurrency(comm.value, comm.currency_type)}`}
                            />
                            <ListItemSecondaryAction>
                              <Tooltip title="Eliminar comisión">
                                <IconButton 
                                  edge="end" 
                                  aria-label="delete"
                                  onClick={() => handleDeleteCustomCommission(comm.id)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </ListItemSecondaryAction>
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                  
                  {/* Resumen de comisiones */}
                  {(commission || customCommissions.length > 0) && (
                    <Box display="flex" justifyContent="flex-end" alignItems="center" mb={2}>
                      <Alert severity="success">
                        <Typography variant="subtitle1">
                          Total con comisiones: {formatCurrency(calculateTotalWithCommissions(values.amount), values.currency_type)}
                        </Typography>
                      </Alert>
                    </Box>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    name="comments"
                    label="Comentarios"
                    fullWidth
                    variant="outlined"
                    multiline
                    rows={3}
                    error={touched.comments && Boolean(errors.comments)}
                    helperText={touched.comments && errors.comments}
                  />
                </Grid>

                <Grid item xs={12} sx={{ mt: 2 }}>
                  <Box display="flex" justifyContent="flex-end">
                    <Button
                      variant="outlined"
                      onClick={handleCancel}
                      sx={{ mr: 1 }}
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={isSubmitting}
                      startIcon={isSubmitting ? <CircularProgress size={20} /> : <SaveIcon />}
                    >
                      {isSubmitting ? 'Guardando...' : 'Crear Transferencia'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Paper>
      
      {/* Diálogo para añadir comisión personalizada */}
      <Dialog open={openCommissionDialog} onClose={handleCloseCommissionDialog}>
        <DialogTitle>Añadir Comisión Personalizada</DialogTitle>
        <DialogContent>
          <Box py={1}>
            <TextField
              margin="normal"
              fullWidth
              label="Valor de la Comisión"
              type="number"
              InputProps={{
                startAdornment: (
                  <Typography variant="body1" sx={{ mr: 1 }}>
                    {currentCurrencyType === 'PEN' ? 'S/.' : '$'}
                  </Typography>
                ),
              }}
              value={customCommissionValue}
              onChange={(e) => setCustomCommissionValue(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCommissionDialog}>Cancelar</Button>
          <Button 
            onClick={handleAddCustomCommission} 
            color="primary" 
            disabled={!customCommissionValue || isNaN(parseFloat(customCommissionValue)) || parseFloat(customCommissionValue) <= 0}
          >
            Añadir
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TransferForm;